/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Palette, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mockSmtp, fetchSmtpLog, getWhitelabelOverride, setWhitelabelOverride } from '@/lib/devTools';

const WhitelabelSection = () => {
  const [mode, setMode] = useState(getWhitelabelOverride());
  const toggle = () => {
    const next = mode === 'whitelabel' ? 'standard' : 'whitelabel';
    setWhitelabelOverride(next); setMode(next);
    toast.info(`Modalità: ${next.toUpperCase()} · ricarica per applicare`);
  };
  return (
    <section className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center gap-2"><Palette className="h-3.5 w-3.5" /><div className="text-xs font-bold">White-Label toggle</div></div>
      <div className="text-[10px] text-muted-foreground">Override client-side di <code>REACT_APP_MODE</code>. La modalità corrente influenza mock AI, WebRTC loop e billing preview.</div>
      <div className="flex items-center gap-2">
        <Badge variant={mode === 'whitelabel' ? 'default' : 'outline'} className="text-[10px]">{mode.toUpperCase()}</Badge>
        <Button size="sm" variant="outline" onClick={toggle} data-testid="whitelabel-toggle" className="flex-1">Passa a {mode === 'whitelabel' ? 'STANDARD' : 'WHITELABEL'}</Button>
      </div>
    </section>
  );
};

const SmtpMockSection = ({ busy, withBusy, status, smtpEntries, setSmtpEntries }) => {
  const [form, setForm] = useState({ to: 'test@sanitanexus.it', subject: 'Magic Link SanitàNexus', body: 'Ciao! Ecco il tuo Magic Link di accesso: https://…\n(Questo è un log di sviluppo)' });
  const run = () => withBusy(async () => {
    await mockSmtp(form);
    toast.success(`Email simulata → ${form.to}`);
    const log = await fetchSmtpLog(5);
    setSmtpEntries(log.entries || []);
  }, 'SMTP mock fallito');
  return (
    <section className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /><div className="text-xs font-bold">SMTP mock (Magic Link)</div></div>
      <div className="text-[10px] text-muted-foreground">Genera un log email senza chiamare Gmail. SMTP dev-mode: <b>{status?.smtp_dev_mode ? 'ON' : 'OFF'}</b></div>
      <div><Label className="text-[10px]">Destinatario</Label><Input className="h-7 text-xs" value={form.to} onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))} data-testid="smtp-to" /></div>
      <div><Label className="text-[10px]">Oggetto</Label><Input className="h-7 text-xs" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} /></div>
      <Button size="sm" onClick={run} disabled={busy} className="w-full" data-testid="smtp-run-btn"><Mail className="h-3 w-3 mr-1" /> Simula invio</Button>
      {smtpEntries.length > 0 && (
        <div className="mt-1 space-y-1 max-h-40 overflow-y-auto scrollbar-thin">
          <div className="text-[9px] uppercase font-bold text-muted-foreground">Ultime email simulate</div>
          {smtpEntries.map((e) => (
            <div key={e.id} className="rounded bg-muted/40 px-2 py-1 text-[10px] font-mono">
              <div className="truncate">→ {e.to}</div>
              <div className="text-muted-foreground truncate">{e.subject}</div>
              <div className="text-muted-foreground text-[9px]">{new Date(e.sent_at).toLocaleTimeString('it')}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/** Tab "Config": toggle white-label, SMTP mock, nota sul gating. */
export const ConfigTab = (props) => (
  <>
    <WhitelabelSection />
    <SmtpMockSection {...props} />
    <section className="rounded-lg border border-dashed border-border p-3">
      <div className="text-[10px] text-muted-foreground leading-snug">
        <b>Gating:</b> il pannello è visibile solo se <code>REACT_APP_ENABLE_DEV_TOOLS=true</code> lato build E l'utente ha <code>is_dev_admin=true</code> lato DB.
        In produzione entrambi sono disattivati → sidebar mai renderizzata, endpoint <code>/api/dev/*</code> non registrati.
      </div>
    </section>
  </>
);
